<script setup lang="ts">
  import { ref, watchEffect } from 'vue'
  import type { Section } from '@/types'

  const props = defineProps<{
    sections: Section[],
    activeSection: string,
    scrollToSection: (id: string) => void,
  }>()

  const logoUrl = '/assets/logo.webp'

  const currentBgIndex = ref(0)

  watchEffect(() => {
    const sectionIndex = props.sections.findIndex((s) => s.id === props.activeSection)
    if (sectionIndex >= 0 && sectionIndex !== currentBgIndex.value) {
      currentBgIndex.value = sectionIndex
    }
  })
</script>

<template>
  <div class="w-full md:w-80 text-white p-6 lg:flex flex-col rounded-l-4xl overflow-hidden relative border-8 border-white py-8 hidden">
    <div
      class="bg-sidebar"
      :class="{ 'opacity-0': currentBgIndex > 0 }"></div>
    <div
      class="bg-sidebar"
      :class="{ 'opacity-0': currentBgIndex > 1 }"></div>
    <div
      class="bg-sidebar"
      :class="{ 'opacity-0': currentBgIndex > 2 }"></div>

    <!-- Content -->
    <div class="relative z-10 flex flex-col h-full">
      <div class="mb-8">
        <img
          :src="logoUrl"
          alt="Gradin Logo"
          class="h-10">
      </div>

      <nav
        class="mt-auto"
        aria-label="Section navigation">
        <ul class="relative space-y-4">
          <!-- vertical line -->
          <div class="absolute w-[1.5px] transform -translate-x-1/2 h-full bg-secondary-1/50"></div>

          <!-- indicator -->
          <div
            class="absolute w-1 bg-primary-500 transition-transform duration-300 ease-in-out rounded-full"
            :class="{ '-translate-y-2': props.sections[0]?.id !== props.activeSection }"
            :style="{
              height: '28px',
              transform: `translate(-50%, ${
                sections.findIndex((s) => s.id === activeSection) * 44
              }px)`,
            }"></div>

          <li
            v-for="section in sections"
            :key="section.id"
            class="relative">
            <a
              href="#"
              class="block px-6 transition-all duration-200 font-bold"
              :class="[
                activeSection === section.id
                  ? 'font-semibold text-white'
                  : 'text-secondary-2',
              ]"
              :aria-current="activeSection === section.id ? 'page' : undefined"
              @click.prevent="scrollToSection(section.id)">
              {{ section.title }}
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .bg-sidebar {
    @apply absolute inset-0 bg-cover bg-center;
    @apply transition-opacity ease-linear duration-400;

    --bg-sidebar-gradient:
      linear-gradient(
        to bottom,
        rgb(0 0 0 / 70%),
        rgb(0 0 0 / 0%) 20%,
        rgb(0 0 0 / 60%) 80%,
        rgb(0 0 0 / 90%)
      );
  }
  .bg-sidebar:nth-child(1) {
    z-index: 3;
    background-image: var(--bg-sidebar-gradient), url("/assets/sidebar1.webp");
  }
  .bg-sidebar:nth-child(2) {
    z-index: 2;
    background-image: var(--bg-sidebar-gradient), url("/assets/sidebar2.webp");
  }
  .bg-sidebar:nth-child(3) {
    z-index: 1;
    background-image: var(--bg-sidebar-gradient), url("/assets/sidebar3.webp");
  }
</style>
